import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';

/** O que o serviço informa ao abrir: a versão publicada, quando o backend a anunciou. */
export interface AtualizacaoDisponivelData {
  versaoPublicada: string | null;
}

/** O que o aviso devolve ao fechar, para o serviço saber se atualiza ou se silencia. */
export type AtualizacaoDisponivelAcao = 'atualizar' | 'depois';

/**
 * Aviso de que existe versão nova — o degrau de baixo, para quando a atualização ainda é opcional.
 *
 * <p>É um snackbar próprio, e não o de uma ação só do Material, porque este caso precisa de duas
 * saídas: quem quer atualizar agora e quem está no meio de alguma coisa. Sem o "Depois", a única
 * forma de tirar o aviso da tela era recarregando — o oposto de não atrapalhar.</p>
 *
 * <p>Não fecha sozinho, não rouba o foco e não recarrega nada por conta própria: quem decide é
 * quem está usando. Um formulário preenchido, um filtro montado ou um upload em curso valem mais
 * do que a versão nova chegar alguns minutos antes.</p>
 */
@Component({
  selector: 'fury-atualizacao-disponivel-snackbar',
  template: `
    <div class="conteudo">
      <mat-icon class="icone">rocket_launch</mat-icon>
      <span class="texto">
        @if (data.versaoPublicada) {
          Versão {{ data.versaoPublicada }} disponível.
        } @else {
          Nova versão disponível.
        }
      </span>
    </div>
    <div class="acoes">
      <button mat-button type="button" (click)="depois()">DEPOIS</button>
      <button mat-button class="destaque" type="button" (click)="atualizar()">ATUALIZAR AGORA</button>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }
    .conteudo { display: flex; align-items: center; gap: 8px; }
    .icone { font-size: 20px; width: 20px; height: 20px; }
    .acoes { display: flex; gap: 4px; margin-left: auto; }

    /*
     * O snackbar inverte as cores da página: fundo escuro nos temas claros, fundo claro no tema
     * escuro. O rótulo do botão, porém, vem do token do tema — o da página, não o do aviso —, e
     * fica preto sobre o cinza escuro nos temas claros e branco sobre branco no escuro, onde
     * simplesmente desaparece.
     *
     * Herdar a cor do próprio aviso resolve nos cinco temas de uma vez: é a mesma cor que o
     * Material já calculou para o texto ao lado, que tem contraste garantido com o fundo.
     */
    .acoes button {
      --mdc-text-button-label-text-color: currentColor;
      color: inherit;
    }

    /* O botão que age fica em destaque; o "Depois" continua legível, mas sem competir. */
    .destaque { font-weight: 600; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule],
})
export class AtualizacaoDisponivelSnackbarComponent {

  readonly data = inject<AtualizacaoDisponivelData>(MAT_SNACK_BAR_DATA);
  private readonly snackbarRef =
    inject<MatSnackBarRef<AtualizacaoDisponivelSnackbarComponent>>(MatSnackBarRef);

  atualizar(): void {
    this.snackbarRef.dismissWithAction();
  }

  /** Fecha sem ação: o serviço lê isso como "silencie por um tempo". */
  depois(): void {
    this.snackbarRef.dismiss();
  }
}
